import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../api/apiConfig';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import {
  CalendarIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import {
  format,
  startOfYear,
  endOfYear,
  eachDayOfInterval,
  getWeek,
  getDay,
  isBefore,
  isAfter,
} from 'date-fns';

interface HabitProgressGraphProps {
  habitId: number;
  habitName: string;
}

interface DayData {
  date: Date;
  status: 'completed' | 'not_completed' | 'pending' | 'not_tracked';
  count?: number;
}

interface HabitInstance {
  id: number;
  date: string;
  completed: boolean;
  notes?: string;
}

const HabitProgressGraph: React.FC<HabitProgressGraphProps> = ({ habitId, habitName }) => {
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [yearData, setYearData] = useState<DayData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLegend, setShowLegend] = useState(true);
  const [hoveredDay, setHoveredDay] = useState<DayData | null>(null);

  // Habit tracking cutoff date - before this date, habits are not tracked

  const fetchYearData = useCallback(async (year: number) => {
    const CUTOFF_DATE = new Date(2025, 5, 27); // June 27, 2025
    setLoading(true);
    setError(null);

    try {
      const startDate = startOfYear(new Date(year, 0, 1));
      const endDate = endOfYear(new Date(year, 0, 1));

      // Don't fetch data before cutoff date
      const actualStartDate = isBefore(startDate, CUTOFF_DATE) ? CUTOFF_DATE : startDate;

      const startDateStr = format(actualStartDate, 'yyyy-MM-dd');
      const endDateStr = format(endDate, 'yyyy-MM-dd');

      // Fetch instances for the year
      const instancesResponse = await apiClient.get(
        `/api/habit-instances/?habit_id=${habitId}&start_date=${startDateStr}&end_date=${endDateStr}`
      );

      const instances: HabitInstance[] = instancesResponse.data || [];

      // Create all days for the year
      const allDays = eachDayOfInterval({ start: startDate, end: endDate });

      // Create lookup for instance data
      const instanceLookup = new Map<string, HabitInstance>();
      instances.forEach(instance => {
        instanceLookup.set(instance.date, instance);
      });

      // Generate day data
      const dayData: DayData[] = allDays.map(date => {
        const dateStr = format(date, 'yyyy-MM-dd');
        const instance = instanceLookup.get(dateStr);

        // Determine status
        let status: DayData['status'];

        if (isBefore(date, CUTOFF_DATE)) {
          status = 'not_tracked';
        } else if (isAfter(date, new Date())) {
          status = 'not_tracked';
        } else if (instance) {
          status = instance.completed ? 'completed' : 'not_completed';
        } else {
          // No instance = pending (should have been tracked but wasn't)
          status = 'pending';
        }

        return {
          date,
          status,
          count: status === 'completed' ? 1 : 0
        };
      });

      setYearData(dayData);
    } catch (err) {
      console.error('Error fetching habit data:', err);
      setError('Failed to load habit progress data');
    } finally {
      setLoading(false);
    }
  }, [habitId]);

  useEffect(() => {
    fetchYearData(currentYear);
  }, [currentYear, fetchYearData]);

  const getIntensityLevel = (status: string, count?: number): number => {
    switch (status) {
      case 'completed':
        return 4; // Darkest green
      case 'not_completed':
        return -1; // Red
      case 'pending':
        return -2; // Yellow
      case 'not_tracked':
      default:
        return 0; // Light gray
    }
  };

  const getColorStyle = (intensityLevel: number): React.CSSProperties => {
    switch (intensityLevel) {
      case 4:
        return { backgroundColor: COLORS.status.success }; // Completed - green
      case -1:
        return { backgroundColor: COLORS.status.error }; // Not completed - red
      case -2:
        return { backgroundColor: COLORS.status.warning }; // Pending - yellow/orange
      case 0:
      default:
        return { backgroundColor: COLORS.bg.hover }; // Not tracked - gray
    }
  };

  const formatTooltip = (day: DayData): string => {
    const dateStr = format(day.date, 'MMM d, yyyy');
    switch (day.status) {
      case 'completed':
        return `${dateStr}: Completed`;
      case 'not_completed':
        return `${dateStr}: Not completed`;
      case 'pending':
        return `${dateStr}: Pending`;
      case 'not_tracked':
        return `${dateStr}: Not tracked`;
      default:
        return dateStr;
    }
  };

  // Organize days into a grid (53 weeks x 7 days)
  const getGridData = () => {
    const grid: (DayData | null)[][] = [];

    // Initialize 53 weeks x 7 days grid
    for (let week = 0; week < 53; week++) {
      grid[week] = new Array(7).fill(null);
    }

    yearData.forEach(day => {
      const weekNumber = getWeek(day.date, { weekStartsOn: 0 }) - 1; // 0-indexed
      const dayOfWeek = getDay(day.date); // 0 = Sunday

      if (weekNumber >= 0 && weekNumber < 53) {
        grid[weekNumber][dayOfWeek] = day;
      }
    });

    return grid;
  };

  const gridData = getGridData();
  const totalDays = yearData.filter(d => d.status !== 'not_tracked').length;
  const completedDays = yearData.filter(d => d.status === 'completed').length;
  const completionRate = totalDays > 0 ? Math.round((completedDays / totalDays) * 100) : 0;

  const monthLabels = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Common styles
  const cardStyle: React.CSSProperties = {
    backgroundColor: COLORS.bg.panel,
    borderRadius: RADIUS,
    padding: SPACING[24],
  };

  const iconContainerStyle: React.CSSProperties = {
    padding: SPACING[8],
    backgroundColor: '#ECFDF5', // Light green background
    borderRadius: '50%',
  };

  const iconStyle: React.CSSProperties = {
    width: '20px',
    height: '20px',
    color: COLORS.status.success,
  };

  const smallIconStyle: React.CSSProperties = {
    width: '16px',
    height: '16px',
  };

  const largeSpinnerStyle: React.CSSProperties = {
    width: '32px',
    height: '32px',
    color: COLORS.text.tertiary,
  };

  const buttonStyle: React.CSSProperties = {
    padding: SPACING[4],
    color: COLORS.text.tertiary,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
  };

  const buttonDisabledStyle: React.CSSProperties = {
    ...buttonStyle,
    opacity: 0.5,
    cursor: 'not-allowed',
  };

  const legendButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: SPACING[4],
    padding: `${SPACING[4]} ${SPACING[8]}`,
    fontSize: TYPOGRAPHY.size.xs,
    color: COLORS.text.secondary,
    background: 'none',
    border: 'none',
    borderRadius: RADIUS,
    cursor: 'pointer',
    transition: 'background-color 150ms, color 150ms',
  };

  const daySquareBaseStyle: React.CSSProperties = {
    width: '12px',
    height: '12px',
    borderRadius: '2px',
    border: `1px solid ${COLORS.border.default}`,
    cursor: 'pointer',
    transition: 'all 150ms',
  };

  const legendSquareStyle: React.CSSProperties = {
    width: '12px',
    height: '12px',
    borderRadius: '2px',
    border: `1px solid ${COLORS.border.default}`,
  };

  if (loading) {
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px' }}>
          <ArrowPathIcon style={{ ...largeSpinnerStyle, animation: 'spin 1s linear infinite' }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={cardStyle}>
        <div style={{ textAlign: 'center', color: COLORS.status.error }}>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING[24] }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[16] }}>
          <div style={iconContainerStyle}>
            <CalendarIcon style={iconStyle} />
          </div>
          <div>
            <h2 style={{
              fontSize: TYPOGRAPHY.size.lg,
              fontWeight: TYPOGRAPHY.weight.semibold,
              color: COLORS.text.primary,
              margin: 0,
            }}>
              Progress Overview
            </h2>
            <p style={{
              fontSize: TYPOGRAPHY.size.sm,
              color: COLORS.text.secondary,
              margin: 0,
            }}>
              {completedDays} completed days • {completionRate}% completion rate
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[16] }}>
          {/* Year Navigation */}
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
            <button
              onClick={() => setCurrentYear(prev => prev - 1)}
              disabled={currentYear <= 2025}
              style={currentYear <= 2025 ? buttonDisabledStyle : buttonStyle}
            >
              <ArrowLeftIcon style={smallIconStyle} />
            </button>
            <span style={{
              fontSize: TYPOGRAPHY.size.sm,
              fontWeight: TYPOGRAPHY.weight.medium,
              color: COLORS.text.secondary,
              minWidth: '4rem',
              textAlign: 'center',
            }}>
              {currentYear}
            </span>
            <button
              onClick={() => setCurrentYear(prev => prev + 1)}
              disabled={currentYear >= new Date().getFullYear()}
              style={currentYear >= new Date().getFullYear() ? buttonDisabledStyle : buttonStyle}
            >
              <ArrowRightIcon style={smallIconStyle} />
            </button>
          </div>

          {/* Legend Toggle */}
          <button
            onClick={() => setShowLegend(!showLegend)}
            style={legendButtonStyle}
          >
            {showLegend ? (
              <>
                <EyeSlashIcon style={smallIconStyle} />
                <span>Hide legend</span>
              </>
            ) : (
              <>
                <EyeIcon style={smallIconStyle} />
                <span>Show legend</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Progress Grid */}
      <div style={{ position: 'relative' }}>
        {/* Month labels */}
        <div style={{ display: 'flex', marginBottom: SPACING[8], fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary }}>
          <div style={{ width: '32px' }}></div> {/* Space for day labels */}
          {monthLabels.map((month, index) => (
            <div key={month} style={{ flex: 1, textAlign: 'left', marginLeft: index === 0 ? '0px' : '8px' }}>
              {month}
            </div>
          ))}
        </div>

        <div style={{ display: 'flex' }}>
          {/* Day labels */}
          <div style={{ display: 'flex', flexDirection: 'column', fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary, marginRight: SPACING[8] }}>
            {dayLabels.map((day, index) => (
              <div key={day} style={{ height: '12px', display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                {index % 2 === 1 ? day.slice(0, 3) : ''}
              </div>
            ))}
          </div>

          {/* Grid */}
          <div style={{ display: 'flex', gap: SPACING[4] }}>
            {gridData.map((week, weekIndex) => (
              <div key={weekIndex} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {week.map((day, dayIndex) => (
                  <div
                    key={`${weekIndex}-${dayIndex}`}
                    style={{
                      ...daySquareBaseStyle,
                      ...(day ? getColorStyle(getIntensityLevel(day.status, day.count)) : { backgroundColor: COLORS.bg.base }),
                    }}
                    title={day ? formatTooltip(day) : ''}
                    onMouseEnter={() => setHoveredDay(day)}
                    onMouseLeave={() => setHoveredDay(null)}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Hover tooltip */}
        {hoveredDay && (
          <div style={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            marginBottom: SPACING[8],
            padding: `${SPACING[4]} ${SPACING[8]}`,
            backgroundColor: COLORS.text.primary,
            color: COLORS.bg.panel,
            fontSize: TYPOGRAPHY.size.xs,
            borderRadius: RADIUS,
            zIndex: 10,
          }}>
            {formatTooltip(hoveredDay)}
          </div>
        )}
      </div>

      {/* Legend */}
      {showLegend && (
        <div style={{
          marginTop: SPACING[24],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: TYPOGRAPHY.size.xs,
          color: COLORS.text.secondary,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
            <span>Less</span>
            <div style={{ display: 'flex', gap: SPACING[4] }}>
              <div style={{ ...legendSquareStyle, backgroundColor: COLORS.bg.hover }}></div>
              <div style={{ ...legendSquareStyle, backgroundColor: COLORS.status.warning }}></div>
              <div style={{ ...legendSquareStyle, backgroundColor: COLORS.status.error }}></div>
              <div style={{ ...legendSquareStyle, backgroundColor: COLORS.status.success }}></div>
            </div>
            <span>More</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[16] }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[4] }}>
              <div style={{ ...legendSquareStyle, backgroundColor: COLORS.bg.hover }}></div>
              <span>No data</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[4] }}>
              <div style={{ ...legendSquareStyle, backgroundColor: COLORS.status.warning }}></div>
              <span>Pending</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[4] }}>
              <div style={{ ...legendSquareStyle, backgroundColor: COLORS.status.error }}></div>
              <span>Missed</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[4] }}>
              <div style={{ ...legendSquareStyle, backgroundColor: COLORS.status.success }}></div>
              <span>Completed</span>
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div style={{
        marginTop: SPACING[24],
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: SPACING[16],
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: TYPOGRAPHY.size['2xl'],
            fontWeight: TYPOGRAPHY.weight.bold,
            color: COLORS.text.primary,
          }}>
            {totalDays}
          </div>
          <div style={{
            fontSize: TYPOGRAPHY.size.sm,
            color: COLORS.text.tertiary,
          }}>
            Total days tracked
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: TYPOGRAPHY.size['2xl'],
            fontWeight: TYPOGRAPHY.weight.bold,
            color: COLORS.status.success,
          }}>
            {completedDays}
          </div>
          <div style={{
            fontSize: TYPOGRAPHY.size.sm,
            color: COLORS.text.tertiary,
          }}>
            Days completed
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: TYPOGRAPHY.size['2xl'],
            fontWeight: TYPOGRAPHY.weight.bold,
            color: COLORS.accent.default,
          }}>
            {completionRate}%
          </div>
          <div style={{
            fontSize: TYPOGRAPHY.size.sm,
            color: COLORS.text.tertiary,
          }}>
            Completion rate
          </div>
        </div>
      </div>
    </div>
  );
};

export default HabitProgressGraph;
